import { JSX, memo, useContext, useEffect, useState } from "react";
import { createPortal } from 'react-dom';
import { cloneDeep, get, isEqual } from 'lodash';
import {
  BenchmarkingLineOfBusinessEnum,
} from "@app/overviews/network-benchmarking-dashboard/filters/shared";
import {
  MedicareNetwork,
  MedicareParentOrganization,
  MedicareParentOrganizationSortingTypeEnum,
} from '@app/overviews/network-benchmarking-dashboard/medicare/filters';
import { useMapboxControl } from '@app/shared/mapping/useMapBoxControl';
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { BenchmarkingContext } from "@app/overviews/network-benchmarking-dashboard/BenchmarkingContext";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/BenchmarkingFiltersContext";
import { NETWORK_FILTER_CONTROL_WIDTH } from "@app/overviews/network-benchmarking-dashboard/filters/CompetitiveInsightsMapConstants";
import { CompetitiveInsightsMapContext } from "@app/overviews/network-benchmarking-dashboard/filters/CompetitiveInsightsMapContext";
import CompetitiveInsightsMapNetworks from "@app/overviews/network-benchmarking-dashboard/filters/CompetitiveInsightsMapNetworks";
import CompetitiveInsightsMapOverlapButton from "@app/overviews/network-benchmarking-dashboard/filters/CompetitiveInsightsMapOverlapButton";
import { BenchmarkingNetworkType } from "@app/overviews/network-benchmarking-dashboard/filters/shared/benchmarkingNetworkType";
import { BenchmarkingParentOrganizationType } from "@app/overviews/network-benchmarking-dashboard/filters/shared/benchmarkingParentOrganizationType";
import { CompetitiveInsightsSelectedSortType } from "@app/overviews/network-benchmarking-dashboard/filters/shared/competitiveInsightsSelectedSortType";
import { QHPFiltersContext, QHPFiltersContextValues } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/QHPFiltersContext";
import { QHPNetwork } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/QHPNetwork";
import { QHPParentOrganization } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/QHPParentOrganization";
import { QHPParentOrganizationSortingTypeEnum } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/QHPParentOrganizationSortingTypeEnum";
import { MedicareFiltersContext, MedicareFiltersContextValues } from "@app/overviews/network-benchmarking-dashboard/medicare/filters/MedicareFiltersContext";
import { MedicaidFiltersContext } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/MedicaidFiltersContext";
import { MedicaidParentOrganization } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/shared/MedicaidParentOrganization";
import { MedicaidParentOrganizationSortingTypeEnum } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/shared/MedicaidParentOrganizationSortingTypeEnum";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import TooltipWrapper from "@app/shared/TooltipWrapper";

interface NetworkFilterControlProps {
  mapHeight: number;
  networkFiltersLoading: boolean;
  parentOrganizations: BenchmarkingParentOrganizationType[];
}

const NetworkFilterControl = (props: NetworkFilterControlProps): JSX.Element => {
  const [filteredParentOrganizations, setFilteredParentOrganizations] = useState<BenchmarkingParentOrganizationType[]>([]);
  const [networkSearchValue, setNetworkSearchValue] = useState<string>("");

  const benchmarkingContext = useContext(BenchmarkingContext);
  const competitiveInsightsMapContext = useContext(CompetitiveInsightsMapContext);
  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);
  const medicareFiltersContext = useContext(MedicareFiltersContext);
  const qhpFiltersContext = useContext(QHPFiltersContext);
  const medicaidFiltersContext = useContext(MedicaidFiltersContext);

  const getContextByLineOfBusiness = () => {
    switch (benchmarkingFiltersContext.selectedLineOfBusiness) {
      case BenchmarkingLineOfBusinessEnum.Medicare:
        return medicareFiltersContext;
      case BenchmarkingLineOfBusinessEnum.Marketplace:
        return qhpFiltersContext;
      case BenchmarkingLineOfBusinessEnum.Medicaid:
        return medicaidFiltersContext;
      default:
        return;
    }
  }

  useEffect(() => {
    const ctx = getContextByLineOfBusiness();
    const selectedContracts = benchmarkingFiltersContext.selectedNetworks.map(x => x.versionId).sort();
    const displayedContracts = benchmarkingFiltersContext.displayedNetworkFilters.map(x => x.versionId).sort();

    if (!isEqual(selectedContracts, displayedContracts)) {
      switch (benchmarkingFiltersContext.selectedLineOfBusiness) {
        case BenchmarkingLineOfBusinessEnum.Medicare:
          const filteredMedicare = (ctx.parentOrganizations as MedicareParentOrganization[]).filter(p =>
            p.hContracts.some(
              c => selectedContracts.includes(c.versionId)
            ));

          medicareFiltersContext.setSelectedParentOrganizations(filteredMedicare);
          break;
        case BenchmarkingLineOfBusinessEnum.Marketplace:
          const filteredQhp = (ctx.parentOrganizations as QHPParentOrganization[]).filter(p =>
            p.hContracts.some(
              c => selectedContracts.includes(c.versionId)
            ));

          qhpFiltersContext.setSelectedParentOrganizations(filteredQhp);
          break;
        default:
          return;
      }
    }
    
  }, [benchmarkingFiltersContext.selectedNetworks]);

  useEffect(() => {
    setFilteredParentOrganizations(cloneDeep(props.parentOrganizations));
  }, [props.parentOrganizations]);

  useEffect(() => {
    filterAndSort();
  }, [getContextByLineOfBusiness().selectedSortType]);

  useEffect(() => {
    filterAndSort();
  }, [networkSearchValue]);

  const filterAndSort = (): void => {
    const searchValue = networkSearchValue.toUpperCase();
    let filteredParents: BenchmarkingParentOrganizationType[] = cloneDeep(props.parentOrganizations);

    switch (benchmarkingFiltersContext.selectedLineOfBusiness) {
      case BenchmarkingLineOfBusinessEnum.Marketplace:
        // filter marketplace parent organizations
        filteredParents = filteredParents.filter(po =>
          po.parentOrganizationName.toUpperCase().includes(searchValue) ||
          (po as QHPParentOrganization).hContracts.some(x => x.contractId.toUpperCase().includes(searchValue) || x.networkName.toUpperCase().includes(searchValue))
        );

        // filter marketplace contracts
        for (const fp of (filteredParents as QHPParentOrganization[])) {
          const filteredContracts: QHPNetwork[] = fp.hContracts.filter(
            x => x.contractId.toUpperCase().includes(searchValue) || x.networkName.toUpperCase().includes(searchValue)
          );

          // if there are no filtered contracts, then continue to display all contracts,
          // since it means that it was already filtered by parent organization name
          if (filteredContracts.length > 0) {
            fp.hContracts = filteredContracts;
          }
        }

        break;
      case BenchmarkingLineOfBusinessEnum.Medicare:
        // filter medicare parent organizations
        filteredParents = filteredParents.filter(po =>
          po.parentOrganizationName.toUpperCase().includes(searchValue) ||
          (po as MedicareParentOrganization).hContracts.map(h => h.contractId.toUpperCase()).some(x => x.includes(searchValue))
        );

        // filter medicare contracts
        for (const fp of (filteredParents as MedicareParentOrganization[])) {
          const filteredContracts: MedicareNetwork[] = fp.hContracts.filter(x => x.contractId.toUpperCase().includes(searchValue));

          // if there are no filtered contracts, then continue to display all contracts,
          // since it means that it was already filtered by parent organization name
          if (filteredContracts.length > 0) {
            fp.hContracts = filteredContracts;
          }
        }

        break;
      case BenchmarkingLineOfBusinessEnum.Medicaid:
        // filter medicaid parent organizations
        filteredParents = filteredParents.filter(po =>
          (po.parentOrganizationName?.toUpperCase().includes(searchValue) ?? false) ||
          ((po as MedicaidParentOrganization).hContract?.contractId?.toUpperCase().includes(searchValue) ?? false)
        );

        break;
      default:
        break;
    }

    filteredParents = getContextByLineOfBusiness().sortNetworks(filteredParents);
    setFilteredParentOrganizations(filteredParents);
  }

  const getButtonTop = (): number => {
    const buttonElement = document.getElementById("competitive-insights-dashboard__map-tab--map-container-button");
    const buttonHeight = buttonElement?.clientHeight ?? 0;

    return (competitiveInsightsMapContext.mapRef.current?.getCanvas().getBoundingClientRect().height / 2) - (buttonHeight / 2);
  }

  const getSortTypeEnum = () => {
    switch (benchmarkingFiltersContext.selectedLineOfBusiness) {
      case BenchmarkingLineOfBusinessEnum.Medicare:
        return Object.values(MedicareParentOrganizationSortingTypeEnum);
      case BenchmarkingLineOfBusinessEnum.Marketplace:
        return Object.values(QHPParentOrganizationSortingTypeEnum);
      case BenchmarkingLineOfBusinessEnum.Medicaid:
        return Object.values(MedicaidParentOrganizationSortingTypeEnum);
      default:
        return;
    }
  }

  const clearDisabled = (): boolean => {
    const checkParentOrganizations = () => {
      switch (benchmarkingFiltersContext.selectedLineOfBusiness) {
        case BenchmarkingLineOfBusinessEnum.Medicare:
          return !!(getContextByLineOfBusiness() as MedicareFiltersContextValues).selectedParentOrganizations.length;
        case BenchmarkingLineOfBusinessEnum.Marketplace:
          return !!(getContextByLineOfBusiness() as QHPFiltersContextValues).selectedParentOrganizations.length;
        default:
          return;
      }
    }

    if (!getContextByLineOfBusiness().networkFiltersLoading && (
      checkParentOrganizations() || !!benchmarkingFiltersContext.selectedNetworks.length
    )) {
      return false;
    }

    return true;
  }

  const onSetServiceAreaClick = (): void => {
    const countyIds: number[] = [];
    const networks: BenchmarkingNetworkType[] = [];

    for (const mapOverlapType of competitiveInsightsMapContext.selectedMapOverlapTypes) {
      const mapOverlapTypeData = get(competitiveInsightsMapContext.overlapCounties, mapOverlapType.key);

      if (!!mapOverlapTypeData) {
        countyIds.push(...mapOverlapTypeData.features.map(x => x.properties.countyId));
      }
    }

    for (const network of benchmarkingFiltersContext.selectedNetworks) {
      if (network.networkCounties.some(nc => countyIds.includes(nc.countyId))) {
        networks.push(network);
      }
    }

    switch (benchmarkingFiltersContext.selectedLineOfBusiness) {
      case BenchmarkingLineOfBusinessEnum.Medicare:
        (getContextByLineOfBusiness() as MedicareFiltersContextValues).onApplyServiceAreaClick(countyIds, networks);
        break;
      case BenchmarkingLineOfBusinessEnum.Marketplace:
        (getContextByLineOfBusiness() as QHPFiltersContextValues).onApplyServiceAreaClick(countyIds, networks);
        break;
      default:
        break;
    }
  }

  const submitDisabled = (): boolean => {
    const hContractsEqual = isEqual(
      cloneDeep(benchmarkingFiltersContext.displayedNetworkFilters).sort((a, b) => a.contractId.localeCompare(b.contractId)),
      cloneDeep(benchmarkingFiltersContext.selectedNetworks).sort((a, b) => a.contractId.localeCompare(b.contractId))
    );

    if (!getContextByLineOfBusiness().networkFiltersLoading &&
      benchmarkingFiltersContext.selectedNetworks.length > 1 &&
      benchmarkingFiltersContext.selectedNetworks.length < 6 &&
      !hContractsEqual
    ) {
      return false;
    }

    return true;
  }

  const getNetworksByLineOfBusiness = (parentOrganization: BenchmarkingParentOrganizationType) => {
    switch (benchmarkingFiltersContext.selectedLineOfBusiness) {
      case BenchmarkingLineOfBusinessEnum.Medicare:
        return (parentOrganization as MedicareParentOrganization).hContracts;
      case BenchmarkingLineOfBusinessEnum.Marketplace:
        return (parentOrganization as QHPParentOrganization).hContracts;
      case BenchmarkingLineOfBusinessEnum.Medicaid:
        return [(parentOrganization as MedicaidParentOrganization).hContract];
      default:
        return;
    }
  }

  const container = useMapboxControl({ mapRef: competitiveInsightsMapContext.mapRef, position: "top-left" });
  if (!container) return null;

  return createPortal(
    <div>
      {competitiveInsightsMapContext.isNetworkFilterControlOpen && (
        <div
          id="competitive-insights__map--network-container"
          className="col-12 p-0"
          style={{
            height: `${props.mapHeight}px`,
          }}
        >
          <div className="competitive-insights__map--header">
            <label className="label-s networks-label">
              Networks
            </label>

            <div className="p-2">
              <QESTextInput
                className="mb-3"
                placeholder="Search by name or ID"
                testId="competitive-insights__map--search"
                label="Network Search"
                hideLabel={true}
                onChange={(e) => setNetworkSearchValue(e.target.value)}
                value={networkSearchValue}
              />

              <QESSelect
                testId="network-benchmarking-dashboard__network-filters--parent-organization-sort-by"
                label="Sort By"
                onChange={(e) => getContextByLineOfBusiness().handleSetSelectedSortType(e.target.value as CompetitiveInsightsSelectedSortType)}
                value={getContextByLineOfBusiness().selectedSortType || ""}
              >
                {getSortTypeEnum().map((sortingType, index) => (
                  <option
                    key={index}
                    value={sortingType}
                  >
                    {sortingType}
                  </option>
                ))}
              </QESSelect>
            </div>
          </div>

          <div className="p-2">
            <div className="row">
              <div className="col-12">
                {props.networkFiltersLoading ? (
                  <div className="text-center">
                    <LoadingSpinner />
                  </div>
                ) : (
                  <div>
                    {filteredParentOrganizations.map((parentOrganization, index) => (
                      <CompetitiveInsightsMapNetworks
                        key={index}
                        networks={getNetworksByLineOfBusiness(parentOrganization)}
                        networkSearchValue={networkSearchValue}
                        parentOrganization={parentOrganization}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="d-flex align-items-center justify-content-between competitive-insights__map--footer p-2">
            <QESButton
              disabled={clearDisabled()}
              qesTestId="network-benchmarking-dashboard__network-filters--clear"
              qesType="secondary-danger"
              onClick={() => getContextByLineOfBusiness().onClearNetworkFiltersClick()}
              className="me-3"
            >
              Clear
            </QESButton>

            <QESButton
              disabled={submitDisabled()}
              qesTestId="network-benchmarking-dashboard__network-filters--submit"
              qesType="primary"
              onClick={() => benchmarkingContext.onSubmitNetworkFiltersClick(props.parentOrganizations)}
            >
              Submit
            </QESButton>
          </div>
        </div>
      )}

      {competitiveInsightsMapContext.mapOverlapTypes?.length > 0 && (
        <CompetitiveInsightsMapOverlapButton
          onSetServiceAreaClick={onSetServiceAreaClick}
        />
      )}

      <div
        style={{
          left: (competitiveInsightsMapContext.isNetworkFilterControlOpen ? NETWORK_FILTER_CONTROL_WIDTH : 0) - 16,
          position: "absolute",
          top: getButtonTop(),
        }}
      >
        <QESButton
          id="competitive-insights-dashboard__map-tab--map-container-button"
          onClick={() => competitiveInsightsMapContext.setIsNetworkFilterControlOpen(!competitiveInsightsMapContext.isNetworkFilterControlOpen)}
          qesType="secondary"
          qesTestId="competitive-insights-dashboard__map-tab--map-container-button"
        >
          <TooltipWrapper
            key={competitiveInsightsMapContext.isNetworkFilterControlOpen ? "network-filter-control-open-tooltip" : "network-filter-control-closed-tooltip"}
            text={`${competitiveInsightsMapContext.isNetworkFilterControlOpen ? "Close" : "Open"} Networks`}
          >
            <QesIcon
              iconName={QesIcons.ChevronRight}
              color="#1c78d6"
              height="16"
              style={{
                transform: competitiveInsightsMapContext.isNetworkFilterControlOpen ? "rotate(180deg)" : "rotate(0deg)",
              }}
              testId="competitive-insights-dashboard__map-tab--map-container-button-icon"
              width="16"
              description={`${competitiveInsightsMapContext.isNetworkFilterControlOpen ? "Close" : "Open"} Networks`}
            />
          </TooltipWrapper>
        </QESButton>
      </div>
    </div>,
    container
  )
}

export default memo(NetworkFilterControl);